import React from 'react';
import { Button } from '../../components/ui/button';
import { User } from 'lucide-react';
import { ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';

const TZ_DEFAULT = 'America/Los_Angeles';

export function SupplierDashboardTab({ portalStats, dashDateFrom, setDashDateFrom, dashDateTo, setDashDateTo, onReload }) {
  if (!portalStats) return null;

  return (
    <div>
      <h2 className="text-lg font-bold mb-4">Dashboard</h2>
      {/* Date Range */}
      <div className="flex items-center gap-3 mb-4 bg-white border rounded-lg px-4 py-2">
        <span className="text-xs font-semibold text-slate-500">Date Range:</span>
        <input type="date" value={dashDateFrom} onChange={e => setDashDateFrom(e.target.value)} className="border rounded px-2 py-1 text-xs" />
        <span className="text-xs text-slate-400">to</span>
        <input type="date" value={dashDateTo} onChange={e => setDashDateTo(e.target.value)} className="border rounded px-2 py-1 text-xs" />
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onReload}>Apply</Button>
      </div>
      {/* Top Row: Company + Recent History */}
      <div className="grid grid-cols-5 gap-4 mb-4">
        <div className="col-span-2 space-y-3">
          <div className="border rounded-xl p-4 bg-white">
            <div className="flex items-center gap-3"><User className="w-8 h-8 text-slate-400" /><div>
              <p className="font-bold">{portalStats.company_name}</p>
              <p className="text-xs text-slate-500">{portalStats.email}</p>
            </div></div>
          </div>
          <div className="border rounded-xl p-4 bg-white">
            <p className="text-xs font-bold mb-1">Total Orders</p>
            <p className="text-xs text-slate-500">Orders from {dashDateFrom} to {dashDateTo}</p>
            <p className="text-3xl font-bold">{portalStats.total_orders}</p>
          </div>
          <div className="border rounded-xl p-4 bg-white">
            <p className="text-xs font-bold mb-1">Orders Values</p>
            <p className="text-xs text-slate-500">Total PO value from {dashDateFrom} to {dashDateTo}</p>
            <p className="text-3xl font-bold">{portalStats.total_value?.toLocaleString('en-US', {minimumFractionDigits:2})} <span className="text-sm font-normal text-slate-400">(USD)</span></p>
          </div>
        </div>
        <div className="col-span-3 border rounded-xl p-4 bg-white">
          <h3 className="font-bold text-sm mb-3">Recent History</h3>
          {portalStats.recent_history?.length ? (
            <div className="space-y-2 max-h-[220px] overflow-y-auto">
              {portalStats.recent_history.map((r, i) => (
                <div key={i} className="text-xs border-b pb-1.5">
                  <span className="font-mono text-indigo-600">{r.order_number}</span>
                  <span className="text-slate-500 ml-2">{r.event}</span>
                  <span className="text-slate-400 float-right">{r.event_date ? new Date(r.event_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: TZ_DEFAULT }) : ''}</span>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-slate-400 text-center py-8">No Record Found</p>}
        </div>
      </div>
      {/* Bottom Row: 3 Charts */}
      <div className="grid grid-cols-3 gap-4">
        <div className="border rounded-xl p-4 bg-white">
          <h3 className="font-bold text-sm mb-3">My Orders by Months/Year</h3>
          {portalStats.monthly_orders?.length > 0 ? (
            <>
              <div style={{height: 200}} role="img" aria-label="Line chart showing monthly order values">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={portalStats.monthly_orders}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="name" tick={{fontSize: 10}} />
                    <YAxis tick={{fontSize: 10}} />
                    <Tooltip formatter={(v) => `$${v.toLocaleString()}`} />
                    <Line type="monotone" dataKey="value" stroke="#f97316" strokeWidth={2} name="Order Value" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <details className="mt-1"><summary className="text-[10px] text-slate-400 cursor-pointer">View data table</summary>
                <table className="w-full text-[10px] mt-1"><tbody>{portalStats.monthly_orders.map((m,i) => <tr key={i} className="border-t"><td className="py-0.5">{m.name}</td><td className="text-right">${m.value?.toLocaleString()}</td></tr>)}</tbody></table>
              </details>
            </>
          ) : <p className="text-xs text-slate-400 text-center py-8">No data</p>}
        </div>
        <div className="border rounded-xl p-4 bg-white">
          <h3 className="font-bold text-sm mb-3">Top Ordered Items</h3>
          {portalStats.top_items?.length > 0 ? (
            <>
              <div style={{height: 200}} role="img" aria-label="Bar chart showing top ordered items by quantity">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={portalStats.top_items}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="name" tick={{fontSize: 9}} />
                    <YAxis tick={{fontSize: 10}} />
                    <Tooltip />
                    <Bar dataKey="qty" fill="#6366f1" name="Quantity" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <details className="mt-1"><summary className="text-[10px] text-slate-400 cursor-pointer">View data table</summary>
                <table className="w-full text-[10px] mt-1"><tbody>{portalStats.top_items.map((m,i) => <tr key={i} className="border-t"><td className="py-0.5">{m.name}</td><td className="text-right">{m.qty}</td></tr>)}</tbody></table>
              </details>
            </>
          ) : <p className="text-xs text-slate-400 text-center py-8">No data found for the selected criteria</p>}
        </div>
        <div className="border rounded-xl p-4 bg-white">
          <h3 className="font-bold text-sm mb-3">Overdue Items</h3>
          {portalStats.overdue_items?.length > 0 ? (
            <div style={{height: 200}} role="img" aria-label="Bar chart showing overdue items">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={portalStats.overdue_items}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{fontSize: 9}} />
                  <YAxis tick={{fontSize: 10}} />
                  <Tooltip />
                  <Bar dataKey="qty" fill="#16a34a" name="Items" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : <p className="text-xs text-slate-400 text-center py-8">No overdue items</p>}
        </div>
      </div>
      {/* Footer */}
      <div className="text-center mt-6 pt-4 border-t">
        <a href="https://www.eyvo.com" target="_blank" rel="noopener noreferrer" className="text-xs text-slate-400 hover:text-indigo-600 transition-colors">
          Powered by Eyvo eProcurement
        </a>
      </div>
    </div>
  );
}
